import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import defaultClient from '@src/api/clients/default';
import { fetchClients } from '@src/api/actions/clients';
import { fetchClientVerticalOptions } from '@src/api/actions/verticals';
import { fetchFacebookValueRulesPreview } from '@src/api/actions';

const USD_CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatPercentFromFraction(value, digits = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0.0%';
  }
  return `${(numeric * 100).toFixed(digits)}%`;
}

function formatSignedPercent(value, digits = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0%';
  }
  const rounded = numeric.toFixed(digits);
  return `${numeric > 0 ? '+' : ''}${rounded}%`;
}

function BudgetOptimizerPage() {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState('');

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [structure, setStructure] = useState([]);
  const [structureLoading, setStructureLoading] = useState(false);
  const [structureError, setStructureError] = useState('');

  const [territories, setTerritories] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(false);
  const [territoriesError, setTerritoriesError] = useState('');

  const [verticals, setVerticals] = useState([]);
  const [verticalsLoading, setVerticalsLoading] = useState(true);
  const [verticalsError, setVerticalsError] = useState('');

  const [selectedAdAccountId, setSelectedAdAccountId] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(false);
  const [selectedVerticalIds, setSelectedVerticalIds] = useState([]);
  const [selectedTerritoryIds, setSelectedTerritoryIds] = useState([]);

  const [optimizerPreview, setOptimizerPreview] = useState(null);
  const [optimizerLoading, setOptimizerLoading] = useState(false);
  const [optimizerError, setOptimizerError] = useState('');
  const [optimizerLookbackDays, setOptimizerLookbackDays] = useState(30);
  const [optimizerMinDmaLeads, setOptimizerMinDmaLeads] = useState(8);
  const [optimizerMinCityLeads, setOptimizerMinCityLeads] = useState(12);
  const [optimizerPacingTolerancePct, setOptimizerPacingTolerancePct] = useState(15);

  useEffect(() => {
    setClientsLoading(true);
    fetchClients()
      .then((response) => {
        setClients(response.data);
      })
      .catch((error) => {
        setClientsError(error?.message || 'Failed to load clients.');
      })
      .finally(() => setClientsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setVerticals([]);
      setSelectedVerticalIds([]);
      return;
    }

    setVerticalsLoading(true);
    setVerticalsError('');
    fetchClientVerticalOptions({ clientId: selectedClientId })
      .then((response) => {
        const fetched = response.data || [];
        setVerticals(fetched);
        setSelectedVerticalIds(fetched.map((vertical) => vertical.id));
      })
      .catch((error) => {
        setVerticalsError(error?.message || 'Failed to load verticals.');
      })
      .finally(() => setVerticalsLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) {
      setStructure([]);
      setSelectedAdAccountId('');
      setSelectedCampaignIds([]);
      return;
    }

    setStructureLoading(true);
    setStructureError('');
    defaultClient
      .request({
        url: `dashboard/facebook/ad-structure/?client_id=${selectedClientId}&include_inactive=${showInactiveCampaigns}`,
        method: 'GET',
      })
      .then((response) => {
        const fetchedStructure = response.data || [];
        setStructure(fetchedStructure);
      })
      .catch((error) => {
        setStructureError(error?.message || 'Failed to load Facebook structure.');
      })
      .finally(() => setStructureLoading(false));
  }, [selectedClientId, showInactiveCampaigns]);

  useEffect(() => {
    if (!selectedClientId) {
      setTerritories([]);
      return;
    }

    setTerritoriesLoading(true);
    setTerritoriesError('');
    setSelectedTerritoryIds([]);
    defaultClient
      .request({
        url: `dashboard/client-territories/?client_id=${selectedClientId}`,
        method: 'GET',
      })
      .then((response) => {
        setTerritories(response.data);
      })
      .catch((error) => {
        setTerritoriesError(error?.message || 'Failed to load territories.');
      })
      .finally(() => setTerritoriesLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!territories.length) {
      setSelectedTerritoryIds([]);
      return;
    }
    setSelectedTerritoryIds(territories.map((territory) => territory.id));
  }, [territories]);

  useEffect(() => {
    if (!structure.length) {
      setSelectedAdAccountId('');
      return;
    }
    setSelectedAdAccountId((prev) => prev || structure[0].account_id);
  }, [structure]);

  const selectedAccount = useMemo(
    () => structure.find((account) => account.account_id === selectedAdAccountId) || null,
    [structure, selectedAdAccountId]
  );

  useEffect(() => {
    const campaigns = selectedAccount?.campaigns || [];
    if (!campaigns.length) {
      setSelectedCampaignIds([]);
      return;
    }
    setSelectedCampaignIds((prev) => {
      const validPrev = prev.filter((id) =>
        campaigns.some((campaign) => campaign.campaign_id === id)
      );
      return validPrev.length ? validPrev : [campaigns[0].campaign_id];
    });
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedClientId) {
      setOptimizerPreview(null);
      setOptimizerError('');
      return;
    }

    setOptimizerLoading(true);
    setOptimizerError('');

    fetchFacebookValueRulesPreview({
      clientId: selectedClientId,
      campaignIds: selectedCampaignIds,
      verticalIds: selectedVerticalIds,
      territoryIds: selectedTerritoryIds,
      lookbackDays: optimizerLookbackDays,
      minDmaLeads: optimizerMinDmaLeads,
      minCityLeads: optimizerMinCityLeads,
      pacingTolerancePct: optimizerPacingTolerancePct,
    })
      .then((response) => {
        setOptimizerPreview(response.data || null);
      })
      .catch((error) => {
        setOptimizerPreview(null);
        setOptimizerError(error?.message || 'Failed to load optimizer preview.');
      })
      .finally(() => setOptimizerLoading(false));
  }, [
    selectedClientId,
    selectedCampaignIds,
    selectedVerticalIds,
    selectedTerritoryIds,
    optimizerLookbackDays,
    optimizerMinDmaLeads,
    optimizerMinCityLeads,
    optimizerPacingTolerancePct,
  ]);

  const accountOptions = structure;
  const campaignOptions = selectedAccount?.campaigns || [];
  const territoryPacingRows = optimizerPreview?.territory_pacing || [];
  const dmaRecommendationRows = optimizerPreview?.dma_recommendations || [];
  const cityRecommendationRows = optimizerPreview?.city_recommendations || [];
  const campaignBudgetRecommendationRows =
    optimizerPreview?.campaign_budget_recommendations || [];
  const optimizerSummary = optimizerPreview?.summary || null;

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            title="Budget Optimizer"
            subheader="Preview territory pacing, DMA and city value-rule recommendations, and campaign budget targets"
          />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              {clientsError && <Alert severity="error">{clientsError}</Alert>}
              {structureError && <Alert severity="error">{structureError}</Alert>}
              {territoriesError && <Alert severity="error">{territoriesError}</Alert>}
              {verticalsError && <Alert severity="error">{verticalsError}</Alert>}
              {optimizerError && <Alert severity="error">{optimizerError}</Alert>}

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: { xs: 'stretch', md: 'flex-start' } }}
              >
                <Box sx={{ flex: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="optimizer-client-select-label">Client</InputLabel>
                    <Select
                      labelId="optimizer-client-select-label"
                      value={selectedClientId || ''}
                      label="Client"
                      onChange={(event) => setSelectedClientId(event.target.value)}
                      disabled={clientsLoading}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {clientsLoading && <CircularProgress size={18} />}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="optimizer-account-select-label">Facebook Ad Account</InputLabel>
                    <Select
                      labelId="optimizer-account-select-label"
                      value={selectedAdAccountId || ''}
                      label="Facebook Ad Account"
                      onChange={(event) => setSelectedAdAccountId(event.target.value)}
                      disabled={structureLoading || !accountOptions.length}
                    >
                      {accountOptions.map((account) => (
                        <MenuItem key={account.account_id} value={account.account_id}>
                          {account.account_name || account.account_id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Box>
              </Stack>

              <Stack marginTop={1} direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="optimizer-campaign-select-label">Campaign</InputLabel>
                    <Select
                      labelId="optimizer-campaign-select-label"
                      multiple
                      value={selectedCampaignIds}
                      label="Campaign"
                      onChange={(event) => setSelectedCampaignIds(event.target.value)}
                      disabled={!campaignOptions.length || structureLoading}
                      renderValue={(selected) =>
                        selected
                          .map((id) => {
                            const campaign = campaignOptions.find((item) => item.campaign_id === id);
                            return campaign?.campaign_name || id;
                          })
                          .join(', ')
                      }
                    >
                      {campaignOptions.map((campaign) => (
                        <MenuItem key={campaign.campaign_id} value={campaign.campaign_id}>
                          <Checkbox checked={selectedCampaignIds.includes(campaign.campaign_id)} />
                          <ListItemText
                            primary={
                              `${campaign.campaign_name || campaign.campaign_id} ${
                                campaign.status ? `(${campaign.status})` : ''
                              }`.trim()
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveCampaigns}
                      onChange={(event) => setShowInactiveCampaigns(event.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive campaigns"
                  sx={{ alignSelf: 'center', marginLeft: 1 }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Autocomplete
                  multiple
                  options={verticals}
                  getOptionLabel={(option) => option.vertical || ''}
                  value={verticals.filter((vert) => selectedVerticalIds.includes(vert.id))}
                  onChange={(_, value) => setSelectedVerticalIds(value.map((item) => item.id))}
                  loading={verticalsLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Verticals"
                      placeholder="Select verticals"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  options={territories}
                  getOptionLabel={(option) => option.name || option.territory || ''}
                  value={territories.filter((territory) => selectedTerritoryIds.includes(territory.id))}
                  onChange={(_, value) => setSelectedTerritoryIds(value.map((item) => item.id))}
                  loading={territoriesLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Territories"
                      placeholder="Select territories"
                      fullWidth
                    />
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedTerritoryIds(territories.map((territory) => territory.id))}
                  disabled={!territories.length}
                >
                  All Territories
                </Button>
                <Button
                  color="primary"
                  size="small"
                  variant="text"
                  onClick={() => setSelectedTerritoryIds([])}
                  disabled={!selectedTerritoryIds.length}
                >
                  Clear Territories
                </Button>
              </Stack>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
              >
                <TextField
                  label="Lookback days"
                  type="number"
                  size="small"
                  value={optimizerLookbackDays}
                  onChange={(event) => setOptimizerLookbackDays(event.target.value)}
                  sx={{ width: 140 }}
                  inputProps={{ min: 7, max: 90 }}
                />
                <TextField
                  label="Min DMA leads"
                  type="number"
                  size="small"
                  value={optimizerMinDmaLeads}
                  onChange={(event) => setOptimizerMinDmaLeads(event.target.value)}
                  sx={{ width: 140 }}
                  inputProps={{ min: 1, max: 100 }}
                />
                <TextField
                  label="Min city leads"
                  type="number"
                  size="small"
                  value={optimizerMinCityLeads}
                  onChange={(event) => setOptimizerMinCityLeads(event.target.value)}
                  sx={{ width: 140 }}
                  inputProps={{ min: 1, max: 100 }}
                />
                <TextField
                  label="Pacing tolerance %"
                  type="number"
                  size="small"
                  value={optimizerPacingTolerancePct}
                  onChange={(event) => setOptimizerPacingTolerancePct(event.target.value)}
                  sx={{ width: 160 }}
                  inputProps={{ min: 5, max: 50 }}
                />
              </Stack>

              <Divider />

              {optimizerLoading ? (
                <CircularProgress size={20} />
              ) : optimizerSummary ? (
                <>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Card variant="outlined" sx={{ flex: 1 }}>
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">
                          Overall Vertical Budget
                        </Typography>
                        <Typography variant="h6">
                          {USD_CURRENCY_FORMATTER.format(
                            Number(optimizerSummary.overall_budget_target || 0)
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          MTD {USD_CURRENCY_FORMATTER.format(
                            Number(optimizerSummary.overall_month_to_date_spend || 0)
                          )} / projected {USD_CURRENCY_FORMATTER.format(
                            Number(optimizerSummary.overall_projected_spend || 0)
                          )}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card variant="outlined" sx={{ flex: 1 }}>
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">
                          Budget Pace
                        </Typography>
                        <Typography variant="h6">
                          {formatPercentFromFraction(optimizerSummary.overall_pacing_ratio || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {optimizerSummary.territory_budget_count || 0} territory budgets in scope
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card variant="outlined" sx={{ flex: 1 }}>
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">
                          Daily Campaign Budget
                        </Typography>
                        <Typography variant="h6">
                          {USD_CURRENCY_FORMATTER.format(
                            Number(optimizerSummary.current_daily_budget_total || 0)
                          )}{' '}
                          →{' '}
                          {USD_CURRENCY_FORMATTER.format(
                            Number(optimizerSummary.recommended_daily_budget_total || 0)
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Read-only recommendation across selected campaigns
                        </Typography>
                      </CardContent>
                    </Card>
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Territory Pacing</Typography>
                    {territoryPacingRows.length ? (
                      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Territory</TableCell>
                              <TableCell align="right">Budget</TableCell>
                              <TableCell align="right">MTD Spend</TableCell>
                              <TableCell align="right">Projected Spend</TableCell>
                              <TableCell align="right">Projected Pace</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {territoryPacingRows.map((row) => (
                              <TableRow key={`territory-pacing-${row.territory_id}`}>
                                <TableCell>{row.territory_name}</TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.budget_amount || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.month_to_date_spend || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.projected_spend || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {formatPercentFromFraction(row.pacing_ratio || 0)}
                                </TableCell>
                                <TableCell>{row.status}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">
                        No scoped territory budgets found for the current month.
                      </Typography>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="subtitle2">DMA Bid Recommendations</Typography>
                    {dmaRecommendationRows.length ? (
                      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>DMA</TableCell>
                              <TableCell>Primary Territory</TableCell>
                              <TableCell align="right">Spend</TableCell>
                              <TableCell align="right">Leads</TableCell>
                              <TableCell align="right">Rev / Lead</TableCell>
                              <TableCell align="right">Recommended</TableCell>
                              <TableCell>Confidence</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dmaRecommendationRows.map((row) => (
                              <TableRow key={`dma-rec-${row.dma_id}`}>
                                <TableCell>{row.dma_name}</TableCell>
                                <TableCell>
                                  {row.territory_name || 'Unmapped'}
                                  {row.territory_weight
                                    ? ` (${formatPercentFromFraction(row.territory_weight, 0)})`
                                    : ''}
                                </TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.total_spend || 0))}
                                </TableCell>
                                <TableCell align="right">{row.total_leads || 0}</TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.revenue_per_lead || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {formatSignedPercent(row.recommended_adjustment_pct || 0)}
                                </TableCell>
                                <TableCell>{row.confidence}</TableCell>
                                <TableCell>{row.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">
                        No DMA adjustments cleared the current signal thresholds.
                      </Typography>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="subtitle2">City Outlier Recommendations</Typography>
                    {cityRecommendationRows.length ? (
                      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>City</TableCell>
                              <TableCell align="right">Leads</TableCell>
                              <TableCell align="right">Rev / Lead</TableCell>
                              <TableCell align="right">Recommended</TableCell>
                              <TableCell>Confidence</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cityRecommendationRows.map((row) => (
                              <TableRow key={`city-rec-${row.city}-${row.state}`}>
                                <TableCell>{[row.city, row.state].filter(Boolean).join(', ')}</TableCell>
                                <TableCell align="right">{row.total_leads || 0}</TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.revenue_per_lead || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {formatSignedPercent(row.recommended_adjustment_pct || 0)}
                                </TableCell>
                                <TableCell>{row.confidence}</TableCell>
                                <TableCell>{row.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">
                        No city adjustments cleared the current lead threshold.
                      </Typography>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Campaign Budget Targets</Typography>
                    {campaignBudgetRecommendationRows.length ? (
                      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Campaign</TableCell>
                              <TableCell align="right">Current Daily</TableCell>
                              <TableCell align="right">MTD Spend</TableCell>
                              <TableCell align="right">Projected Spend</TableCell>
                              <TableCell align="right">Recommended Daily</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {campaignBudgetRecommendationRows.map((row) => (
                              <TableRow key={`campaign-budget-${row.campaign_id}`}>
                                <TableCell>{row.campaign_name}</TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.current_daily_budget || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.month_to_date_spend || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.projected_spend || 0))}
                                </TableCell>
                                <TableCell align="right">
                                  {USD_CURRENCY_FORMATTER.format(Number(row.recommended_daily_budget || 0))}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography color="text.secondary">
                        No campaign budget data found for the current selection.
                      </Typography>
                    )}
                  </Stack>
                </>
              ) : (
                <Typography color="text.secondary">
                  Select a client to load optimizer recommendations.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default BudgetOptimizerPage;
